import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { CssBaseline, ThemeProvider } from '@mui/material'
import * as Sentry from '@sentry/react'
import { lazy, Suspense } from 'react'
import { QueryClientProvider } from '@tanstack/react-query'
import App from './App'
import theme from './theme'
import { AuthProvider } from './providers/AuthProvider'
import { ApiAuthProvider } from './providers/ApiAuthProvider'
import { queryClient } from './utils/queryClient'
import { apiClient } from './services/apiClient'
import './index.css'

// Lazy load DevTools only when needed
const ReactQueryDevtoolsProduction = lazy(() =>
  import('@tanstack/react-query-devtools/build/modern/production.js').then((d) => ({
    default: d.ReactQueryDevtools,
  }))
)

// Initialize Sentry
Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE,
  debug: import.meta.env.DEV,
  enabled: import.meta.env.VITE_SENTRY_ENABLED === 'true',
  tracesSampleRate: 0.10,
  beforeSend(event) {
    if (import.meta.env.DEV) {
      console.log('Sentry event:', event);
    }
    return event;
  },
  // PII: SENDS USER IP ADDRESSES AND REQUEST DATA TO SENTRY (US REGION).
  sendDefaultPii: true
});

// Prefetch locations at app startup
queryClient.prefetchQuery({
  queryKey: ['locations'],
  queryFn: () => apiClient.get('/api/locations'),
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <BrowserRouter>
      <AuthProvider>
        <ApiAuthProvider>
          <QueryClientProvider client={queryClient}>
            <ThemeProvider theme={theme}>
              <CssBaseline />
              <App />
              {(import.meta.env.VITE_APP_ENV === 'development' || import.meta.env.VITE_APP_ENV === 'sandbox') && (
                <Suspense fallback={null}>
                  <ReactQueryDevtoolsProduction initialIsOpen={false} buttonPosition="bottom-left" />
                </Suspense>
              )}
            </ThemeProvider>
          </QueryClientProvider>
        </ApiAuthProvider>
      </AuthProvider>
    </BrowserRouter>
  </React.StrictMode>,
) 